import { useEffect, useState, useCallback, useRef, useMemo, useContext } from 'react'
import { useNavigate } from 'react-router-dom'
import moment from 'moment'
import Breadcrumbs from '@/components/Breadcrumbs/index.jsx'
import PageTitle from '@/components/PageTitle/index'
import MasterDataTable from '@/components/MasterDataTable/index'
import Search from '@/components/Search/index'
import InputDropdown from '@/components/InputDropdown/index'
import usePage from '@/hooks/use-page'
import StatusTags from '@/components/Tags/StatusTags'
import {
  getSubmittalListing,
  getSubmittalFilters,
  downloadSubmittalCsv,
} from '@/services/submittalService'
import { SUBMITTAL_STATUS_LABEL } from './constants'
import useDataElements from '@/hooks/useDataElements'
import IconInfoEmpty from '@/assets/svgs/Icon-info.svg?react'
import { ToastContext } from '@/context/ToastContext'

const PAGE_LIMIT = 10

const TABLE_HEADERS = {
  data_element_id: 'Data Element',
  submission_month: 'Submission Month',
  modified: 'Last Modified',
  project_contract_name: 'Project Contract Name',
  status: 'Status',
  action: '',
}

const STATUS_MAP = Object.fromEntries(
  Object.entries(SUBMITTAL_STATUS_LABEL).map(([k, v]) => [v, k])
)

const FILTER_FIELDS = [
  {
    key: 'data_element_id',
    label: 'Data Element',
    placeholder: 'Select Data Element',
    optionKey: 'data_element_ids',
  },
  {
    key: 'project_contract_name',
    label: 'Project Contract Name',
    placeholder: 'Select Project Contract Name',
    optionKey: 'project_contract_names',
  },
  {
    key: 'submission_month',
    label: 'Submission Month',
    placeholder: 'Select Submission Month',
    optionKey: 'submission_months',
  },
  { key: 'status', label: 'Status', placeholder: 'Select Status', optionKey: 'statuses' },
]

const toDropdownOptions = items =>
  items.map((item, index) => ({ id: String(index), label: item, value: item }))

const SubmittalsListingPage = () => {
  const navigate = useNavigate()
  const { addToastMessage } = useContext(ToastContext)
  const { dataElements, isLoading: deLoading } = useDataElements()

  const dataElementNameMap = useMemo(() => {
    if (!dataElements) return {}
    return Object.fromEntries(dataElements.map(el => [el.dataElementId, el.elementName]))
  }, [dataElements])

  const pageTitle = 'Submittals'
  const { breadcrumbsItems } = usePage({
    title: pageTitle,
    breadcrumbs: [
      { label: '', link: '/submittals', isHomePage: true },
      { label: 'Submittals', link: '/submittals' },
    ],
  })

  const [submittalsData, setSubmittalsData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const [filterOptions, setFilterOptions] = useState({
    data_element_ids: [],
    project_contract_names: [],
    submission_months: [],
    statuses: [],
  })

  const [currentPage, setCurrentPage] = useState(1)
  const [pagination, setPagination] = useState({
    totalItems: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false,
  })

  const [sortColumn, setSortColumn] = useState('modified')
  const [sortOrder, setSortOrder] = useState('desc')

  const [searchSubmittals, setSearchSubmittals] = useState('')

  const [selectedFilters, setSelectedFilters] = useState({
    data_element_id: [],
    project_contract_name: [],
    submission_month: [],
    status: [],
  })

  const handleSort = column => {
    if (sortColumn === column) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortColumn(column)
      setSortOrder('asc')
    }
  }

  const getClassName = headerKey => {
    if (sortColumn !== headerKey) return ''
    return sortOrder === 'asc' ? 'is-asc' : 'is-desc'
  }

  const formatValue = (value, headerKey) => {
    if (headerKey === 'data_element_id') {
      return dataElementNameMap[value] || value
    }
    if (headerKey === 'status') {
      return <StatusTags status={STATUS_MAP[value] || value} />
    }
    if (headerKey === 'modified') {
      if (!value) return ''
      return (
        <div>
          <div>{moment(value).format('MM/DD/YYYY')}</div>
          <div>{moment(value).format('hh:mm A')}</div>
        </div>
      )
    }
    return value
  }

  const handleRowClick = item => navigate(`/submittals/${item.id}`)

  const handleDownloadCsv = async item => {
    setLoading(true)
    const { data, error: downloadError } = await downloadSubmittalCsv(item.id, 'csv')
    setLoading(false)
    if (downloadError) {
      addToastMessage(downloadError, 'error', 'dismiss')
      return
    }
    const blob = new Blob([data], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${item.data_element_id}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const handleFilterChange = (key, selectedItems) => {
    setLoading(true)
    setSelectedFilters(prev => ({ ...prev, [key]: selectedItems }))
    setCurrentPage(1)
  }

  const handleSearchChange = value => {
    setLoading(true)
    setSearchSubmittals(value)
    setCurrentPage(1)
  }

  const fetchSubmittals = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_LIMIT,
        sort: sortColumn || 'modified',
        order: sortOrder || 'desc',
      }

      if (searchSubmittals?.trim()) {
        params.search = searchSubmittals
      }

      Object.entries(selectedFilters).forEach(([key, items]) => {
        if (items.length > 0) {
          params[key] = items.map(item => item.value)
        }
      })

      const response = await getSubmittalListing(params)
      if (response.success) {
        setSubmittalsData(response.data)
        setPagination(response.pagination)
      } else {
        setError('Failed to fetch submittals')
      }
    } catch (err) {
      setError(err.message || 'An error occurred while fetching submittals')
      console.error('API Error:', err)
    } finally {
      setLoading(false)
    }
  }, [currentPage, searchSubmittals, selectedFilters, sortColumn, sortOrder])

  const fetchFilters = useCallback(async () => {
    try {
      const response = await getSubmittalFilters()
      if (response.success) {
        setFilterOptions({
          data_element_ids: response.dataElementIds,
          project_contract_names: response.project_contract_names,
          submission_months: response.submission_months,
          statuses: response.statuses,
        })
      }
    } catch (err) {
      console.error('Failed to fetch filters:', err)
    }
  }, [])

  const hasMounted = useRef(false)

  useEffect(() => {
    fetchFilters()
  }, [])

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true
      fetchSubmittals()
      return
    }
    const timer = setTimeout(fetchSubmittals, 1000)
    return () => clearTimeout(timer)
  }, [fetchSubmittals])

  return (
    <>
      <Breadcrumbs breadcrumbItems={breadcrumbsItems} />
      <PageTitle pageTitle={pageTitle} />

      <div className="grid grid-cols-1 phone:grid-cols-2 tablet:grid-cols-4 gap-2 tablet-p:gap-3 tablet:gap-4 w-full">
        {FILTER_FIELDS.map(({ key, label, placeholder, optionKey }) => (
          <div key={key} className="mb-2.5 max-phone:mb-0 flex-1 min-w-0">
            <div className="font-avenir mb-1 type-meta-medium text-grey-50">{label}</div>
            <InputDropdown
              placeholder={placeholder}
              name={key}
              dropdownOption={
                key === 'data_element_id'
                  ? filterOptions[optionKey].map((id, index) => ({
                      id: String(index),
                      label: dataElementNameMap[id] || id,
                      value: id,
                    }))
                  : key === 'status'
                    ? filterOptions[optionKey].map((status, index) => ({
                        id: String(index),
                        label: SUBMITTAL_STATUS_LABEL[status] || status,
                        value: status,
                      }))
                    : toDropdownOptions(filterOptions[optionKey])
              }
              dropdownSelectedOption={selectedFilters[key]}
              onSelectDropdownItem={items => handleFilterChange(key, items)}
              multipleSelection
              fullWidth
              onChange={() => {}}
            />
          </div>
        ))}
      </div>

      <div className="w-full tablet-p:w-1/2 tablet:w-[calc(25%-0.75rem)] mb-4 mt-2 tablet-p:mt-3 tablet:mt-0">
        <Search
          value={searchSubmittals}
          onChange={handleSearchChange}
          onCancel={() => {
            setSearchSubmittals('')
            setCurrentPage(1)
          }}
          id="submittals-search"
        />
      </div>

      <MasterDataTable
        headers={TABLE_HEADERS}
        data={submittalsData}
        currentPage={currentPage}
        totalPages={pagination.totalPages}
        totalItems={pagination.totalItems}
        pageLimit={PAGE_LIMIT}
        onPageChange={setCurrentPage}
        onSort={handleSort}
        sortColumn={sortColumn}
        sortOrder={sortOrder}
        getClassName={getClassName}
        formatValue={formatValue}
        onRowClick={handleRowClick}
        onDownloadCsv={handleDownloadCsv}
        loading={loading || deLoading}
        error={error}
        emptyMessage="No submissions found."
        emptyIcon={<IconInfoEmpty width={32} height={32} />}
      />
    </>
  )
}

export default SubmittalsListingPage
